import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EmptyState, useFocusTrap } from '@pazportal/ui';
import {
  CLOSED_TICKET_STATUSES,
  OPEN_TICKET_STATUSES,
  useCreateTicket,
  useTickets,
  type TicketRecord,
} from '../hooks/useTickets';
import { useLocations } from '../hooks/useLocations';
import { useAccount } from '../contexts/AccountContext';
import { useViewAs } from '../contexts/ViewAsContext';
import { locationPath } from '../lib/locationPaths';
import { BrandLogo } from '../components/BrandLogo';
import { SkeletonRow } from '../components/Skeleton';
import { StatusBadge } from '../components/StatusBadge';
import { useI18n } from '../i18n/I18nProvider';
import type { MessageKey } from '../i18n/messages';
import { formatSolutionType, timeAgo } from '../lib/formatters';

type Priority = 'low' | 'medium' | 'high' | 'critical';
const PRIORITIES: Array<{ value: Priority; key: MessageKey }> = [
  { value: 'low', key: 'tickets.priority.low' },
  { value: 'medium', key: 'tickets.priority.medium' },
  { value: 'high', key: 'tickets.priority.high' },
  { value: 'critical', key: 'tickets.priority.critical' },
];

function SlaCountdown({ deadline }: { deadline: string | null }) {
  const { t } = useI18n();
  if (!deadline) return <span className="text-xs text-text-faint">{t('tickets.no_sla')}</span>;
  const remaining = new Date(deadline).getTime() - Date.now();
  if (remaining <= 0) {
    return <span className="text-xs font-medium text-notification">{t('tickets.sla_breached')}</span>;
  }
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const color = hours < 4 ? 'text-notification' : hours < 24 ? 'text-warning' : 'text-text-muted';
  return (
    <span className={`text-xs font-medium ${color}`}>{t('tickets.remaining', { h: hours, m: minutes })}</span>
  );
}

function TicketRow({ ticket, onOpen, showAccount }: { ticket: TicketRecord; onOpen: () => void; showAccount: boolean }) {
  const { t } = useI18n();
  const siteLabel = ticket.location?.name ?? t('tickets.unassigned');
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full px-4 py-4 text-left transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:px-5"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-text-faint">{ticket.ticket_number}</span>
            <StatusBadge status={ticket.priority ?? 'medium'} />
            <StatusBadge status={ticket.status ?? 'open'} />
          </div>
          <p className="break-words text-sm font-medium text-text">{ticket.title}</p>
          <p className="mt-0.5 text-xs text-text-faint">
            {showAccount && ticket.customer?.name ? `${ticket.customer.name} · ` : ''}
            {siteLabel}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center justify-between gap-1 sm:flex-col sm:items-end sm:justify-start">
          <SlaCountdown deadline={ticket.sla_deadline} />
        </div>
      </div>
    </button>
  );
}

export function TicketsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id: lockedSite } = useParams<{ id?: string }>();
  const [params, setParams] = useSearchParams();
  const { accounts, activeAccount, isPortfolio, ready } = useAccount();
  const { isStaff, viewAsUserId } = useViewAs();
  const inViewAs = isStaff && !!viewAsUserId;

  const openQuery = useTickets({ status: OPEN_TICKET_STATUSES });
  const resolvedQuery = useTickets({ status: CLOSED_TICKET_STATUSES });
  const { data: locationsData } = useLocations();
  const createTicket = useCreateTicket();

  const [showModal, setShowModal] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'medium' as Priority,
    location_id: '',
    deployment_id: '',
  });
  const titleRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Tab stays inside the dialog, Escape closes it, and focus returns to
  // whatever opened it.
  useFocusTrap(dialogRef, showModal, {
    onClose: () => setShowModal(false),
    initialFocusRef: titleRef,
  });

  // Locations are already scoped to the account selection by useLocations.
  const locations = useMemo(() => locationsData?.locations ?? [], [locationsData]);
  const selectedLocation = locations.find((l) => l.id === form.location_id);
  const selectedDeployments = selectedLocation?.deployments ?? [];
  const requireLocation = isPortfolio || locations.length > 1;

  // Deep link from the site panel: /tickets?new=1&location=<id>
  useEffect(() => {
    if (params.get('new') !== '1' || !locationsData) return;
    // In view-as the dialog must not open at all. Consume the parameter so it
    // does not linger in the address bar; the page already explains why
    // creating a ticket is blocked. (The mutation refuses too — this is so the
    // staff member is not handed a form that can only fail.)
    if (inViewAs) {
      setParams({}, { replace: true });
      return;
    }
    const locationId = params.get('location');
    if (locationId && locations.some((l) => l.id === locationId)) {
      setForm((current) => ({ ...current, location_id: locationId, deployment_id: '' }));
    }
    setFormError(null);
    setShowModal(true);
    setParams({}, { replace: true });
  }, [inViewAs, locations, locationsData, params, setParams]);

  useEffect(() => {
    if (!lockedSite) return;
    setForm((current) => (
      current.location_id === lockedSite
        ? current
        : { ...current, location_id: lockedSite, deployment_id: '' }
    ));
  }, [lockedSite]);

  useEffect(() => {
    if (!showModal) return;
    titleRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowModal(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showModal]);

  const openCreate = () => {
    if (inViewAs) return;
    setFormError(null);
    setCreated(null);
    setShowModal(true);
  };

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!form.title.trim()) return;
    if (requireLocation && !form.location_id) {
      setFormError(t('tickets.form.choose_site'));
      return;
    }
    const location = locations.find((l) => l.id === form.location_id);
    const ticketCustomerId = location?.customer_id ?? (isPortfolio ? null : activeAccount?.customer_id ?? null);
    if (!ticketCustomerId) {
      setFormError(t('tickets.form.no_account'));
      return;
    }
    setFormError(null);
    try {
      const result = await createTicket.mutateAsync({
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        customer_id: ticketCustomerId,
        location_id: form.location_id || undefined,
        deployment_id: form.deployment_id || undefined,
      });
      setShowModal(false);
      setForm({ title: '', description: '', priority: 'medium', location_id: '', deployment_id: '' });
      setCreated(result?.ticket_number ?? '');
    } catch (err) {
      // A raw 'read_only' from the mutation is the view-as refusal; say it in
      // words rather than surfacing the internal code.
      const message = err instanceof Error ? err.message : '';
      setFormError(
        message === 'read_only'
          ? t('tickets.viewas_blocked')
          : message || t('tickets.form.failed'),
      );
    }
  }

  // The site filter lives in the URL so a view is bookmarkable and shareable:
  // "my open Charlotte tickets" is a link a director can send a colleague,
  // which it cannot be if the choice only exists in component state.
  const siteFilter = lockedSite || params.get('site') || '';
  const openTicket = (ticketId: string) => {
    navigate(lockedSite ? locationPath(lockedSite, `tickets/${ticketId}`) : `/tickets/${ticketId}`);
  };
  const setSiteFilter = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('site', value);
    else next.delete('site');
    setParams(next, { replace: true });
  };

  const matchesSite = useCallback(
    (ticket: TicketRecord) => !siteFilter || ticket.location_id === siteFilter,
    [siteFilter],
  );

  const allOpen = useMemo(() => openQuery.data ?? [], [openQuery.data]);
  const openTickets = useMemo(() => allOpen.filter(matchesSite), [allOpen, matchesSite]);
  const resolvedTickets = (resolvedQuery.data ?? []).filter(matchesSite);

  // Only sites that actually carry a ticket: a filter offering twelve empty
  // options is a worse list than no filter.
  const sitesWithTickets = useMemo(() => {
    const byId = new Map<string, string>();
    for (const ticket of [...allOpen, ...(resolvedQuery.data ?? [])]) {
      if (ticket.location_id && ticket.location?.name) byId.set(ticket.location_id, ticket.location.name);
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allOpen, resolvedQuery.data]);

  // A site that no longer has tickets would leave the page filtered to nothing
  // with a control showing a name that is not in its own list.
  const filterIsStale = !!siteFilter && !sitesWithTickets.some((site) => site.id === siteFilter);
  const isLoading = !ready || openQuery.isLoading || resolvedQuery.isLoading;

  // Portfolio mode groups by account so a 16-hotel operator can scan by site.
  const groups = useMemo(() => {
    if (!isPortfolio) return [];
    const nameById = new Map(accounts.map((a) => [a.customer_id, a.name] as const));
    // Each account's hotel brand, from its registry-linked location, so the
    // group header can carry the property's logo.
    const brandById = new Map<string, string>();
    for (const loc of locations) {
      const brand = loc.directory?.brand;
      if (brand && !brandById.has(loc.customer_id)) brandById.set(loc.customer_id, brand);
    }
    const byId = new Map<string, TicketRecord[]>();
    for (const ticket of openTickets) {
      const list = byId.get(ticket.customer_id) ?? [];
      list.push(ticket);
      byId.set(ticket.customer_id, list);
    }
    return [...byId.entries()]
      .map(([customerId, tickets]) => ({
        id: customerId,
        name: nameById.get(customerId) ?? tickets[0]?.customer?.name ?? customerId,
        brand: brandById.get(customerId) ?? null,
        tickets,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [accounts, isPortfolio, locations, openTickets]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text">{t('tickets.title')}</h1>
          {isPortfolio && <p className="mt-0.5 text-xs text-text-muted">{t('tickets.portfolio_hint')}</p>}
        </div>
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
          {!lockedSite && sitesWithTickets.length > 1 && (
            <div className="flex items-center gap-2">
              <label htmlFor="ticket-site-filter" className="text-xs text-text-muted">
                {t('tickets.filter_site')}
              </label>
              <select
                id="ticket-site-filter"
                value={filterIsStale ? '' : siteFilter}
                onChange={(event) => setSiteFilter(event.target.value)}
                className="h-9 rounded-sm border border-border bg-surface px-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">{t('tickets.filter_all_sites')}</option>
                {sitesWithTickets.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex flex-col items-start gap-1 sm:items-end">
          <button
            type="button"
            onClick={openCreate}
            disabled={inViewAs || accounts.length === 0}
            aria-describedby={inViewAs ? 'tickets-viewas-note' : undefined}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-text-inverse transition-colors hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('tickets.new')}
          </button>
          {inViewAs && (
            <p id="tickets-viewas-note" className="text-xs text-text-muted">
              {t('tickets.viewas_blocked')}
            </p>
          )}
          </div>
        </div>
      </div>

      {created !== null && (
        <div
          role="status"
          className="flex items-center justify-between gap-3 rounded-md border border-success bg-success-soft px-3 py-2 text-sm text-text"
        >
          <span>{t('tickets.created', { n: created })}</span>
          <button
            type="button"
            onClick={() => setCreated(null)}
            className="min-h-11 px-2 text-xs font-medium text-text-muted underline sm:min-h-6"
          >
            {t('common.close')}
          </button>
        </div>
      )}

      <section aria-labelledby="open-tickets-heading">
        <h2 id="open-tickets-heading" className="mb-3 text-sm font-medium text-text-muted">
          {t('tickets.open')} ({openTickets.length})
        </h2>
        {isLoading ? (
          <div className="rounded-lg border border-border bg-surface p-5">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <EmptyState icon={<CheckIcon />} title={t('account.none')} subtitle={t('account.none_hint')} />
          </div>
        ) : openQuery.isError ? (
          <div role="alert" className="rounded-lg border border-notification bg-notification-soft p-5">
            <p className="text-sm text-notification">
              {t('tickets.error')}
              {openQuery.error instanceof Error ? `: ${openQuery.error.message}` : '.'}
            </p>
          </div>
        ) : openTickets.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <EmptyState
              icon={<CheckIcon />}
              title={t('tickets.empty')}
              subtitle={t('tickets.empty_hint')}
              action={inViewAs ? undefined : { label: t('tickets.new'), onClick: openCreate }}
            />
          </div>
        ) : isPortfolio ? (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.id} className="overflow-hidden rounded-lg border border-border bg-surface">
                <div className="flex items-center gap-3 border-b border-border bg-surface-2 px-4 py-2.5 sm:px-5">
                  <BrandLogo brand={group.brand} name={group.name} className="h-7 flex-shrink-0" />
                  <p className="min-w-0 text-xs font-semibold uppercase tracking-wide text-text-muted">
                    {group.name}
                    <span className="ml-2 font-normal normal-case tracking-normal">
                      {t('tickets.n_open', { n: group.tickets.length })}
                    </span>
                  </p>
                </div>
                <div className="divide-y divide-divider">
                  {group.tickets.map((ticket) => (
                    <TicketRow
                      key={ticket.id}
                      ticket={ticket}
                      showAccount={false}
                      onOpen={() => openTicket(ticket.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-divider rounded-lg border border-border bg-surface">
            {openTickets.map((ticket) => (
              <TicketRow
                key={ticket.id}
                ticket={ticket}
                showAccount={false}
                onOpen={() => openTicket(ticket.id)}
              />
            ))}
          </div>
        )}
      </section>

      {resolvedTickets.length > 0 && (
        <section aria-labelledby="resolved-tickets-heading">
          <h2 id="resolved-tickets-heading" className="mb-3 text-sm font-medium text-text-muted">
            {t('tickets.resolved')}
          </h2>
          <div className="divide-y divide-divider rounded-lg border border-border bg-surface opacity-80">
            {resolvedTickets.slice(0, 5).map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => openTicket(ticket.id)}
                className="w-full px-4 py-3 text-left hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:px-5"
              >
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="flex-shrink-0 font-mono text-xs text-text-faint">{ticket.ticket_number}</span>
                      <p className="truncate text-sm text-text-muted">{ticket.title}</p>
                    </div>
                    <p className="mt-0.5 text-xs text-text-faint">
                      {isPortfolio && ticket.customer?.name ? `${ticket.customer.name} · ` : ''}
                      {ticket.location?.name ?? t('tickets.unassigned')}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-left sm:text-right">
                    <p className="text-xs text-text-faint">
                      {ticket.resolved_at ? t('tickets.resolved_ago', { ago: timeAgo(ticket.resolved_at) }) : ''}
                    </p>
                    {ticket.resolution_notes && (
                      <p className="mt-0.5 truncate text-xs text-text-faint sm:max-w-48">{ticket.resolution_notes}</p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} aria-hidden="true" />
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-ticket-title"
            className="relative max-h-[90vh] w-full overflow-y-auto rounded-t-lg border border-border bg-surface shadow-xl sm:mx-4 sm:max-w-lg sm:rounded-lg"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-surface px-4 py-3 sm:px-6">
              <h2 id="new-ticket-title" className="text-lg font-semibold text-text">
                {t('tickets.form.title')}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                aria-label={t('tickets.form.close')}
                className="inline-flex h-11 w-11 items-center justify-center rounded-md text-text-faint hover:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form
              onSubmit={handleSubmit}
              className="space-y-4 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6"
            >
              {formError && (
                <p role="alert" className="rounded-md border border-notification bg-notification-soft px-3 py-2 text-sm text-notification">
                  {formError}
                </p>
              )}
              <div>
                <label htmlFor="ticket-title" className="mb-1 block text-sm font-medium text-text">
                  {t('tickets.form.subject')} *
                </label>
                <input
                  id="ticket-title"
                  ref={titleRef}
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="min-h-11 w-full rounded-md border border-border bg-bg-elevated px-3 text-sm text-text outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder={t('tickets.form.subject_placeholder')}
                />
              </div>
              <div>
                <label htmlFor="ticket-description" className="mb-1 block text-sm font-medium text-text">
                  {t('tickets.form.description')}
                </label>
                <textarea
                  id="ticket-description"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder={t('tickets.form.description_placeholder')}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="ticket-priority" className="mb-1 block text-sm font-medium text-text">
                    {t('tickets.form.priority')}
                  </label>
                  <select
                    id="ticket-priority"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value as Priority })}
                    className="min-h-11 w-full rounded-md border border-border bg-bg-elevated px-3 text-sm text-text outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {t(p.key)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="ticket-site" className="mb-1 block text-sm font-medium text-text">
                    {t('tickets.form.site')} {requireLocation ? '*' : ''}
                  </label>
                  <select
                    id="ticket-site"
                    required={requireLocation}
                    disabled={Boolean(lockedSite)}
                    value={form.location_id}
                    onChange={(e) => setForm({ ...form, location_id: e.target.value, deployment_id: '' })}
                    className="min-h-11 w-full rounded-md border border-border bg-bg-elevated px-3 text-sm text-text outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    <option value="">{t('tickets.form.site_placeholder')}</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {form.location_id && selectedDeployments.length > 0 && (
                <div>
                  <label htmlFor="ticket-system" className="mb-1 block text-sm font-medium text-text">
                    {t('tickets.form.system')}
                  </label>
                  <select
                    id="ticket-system"
                    value={form.deployment_id}
                    onChange={(e) => setForm({ ...form, deployment_id: e.target.value })}
                    className="min-h-11 w-full rounded-md border border-border bg-bg-elevated px-3 text-sm text-text outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    <option value="">{t('tickets.form.system_placeholder')}</option>
                    {selectedDeployments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {formatSolutionType(d.solution_type)}
                        {d.vendor ? ` (${d.vendor})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="min-h-11 rounded-md px-4 text-sm text-text-muted hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {t('tickets.form.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createTicket.isPending || !form.title.trim()}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-text-inverse transition-colors hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50"
                >
                  {createTicket.isPending ? t('tickets.form.submitting') : t('tickets.form.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
